import React, { useState } from "react";
import { Star, MapPin, ExternalLink, Quote, ChevronDown } from "lucide-react";

interface GoogleReview {
  id: string;
  authorName: string;
  authorPhoto?: string;
  rating: number;
  text: string;
  time: string;
  relativeTime: string;
}

interface GoogleReviewsSectionProps {
  reviews?: GoogleReview[];
  averageRating?: number;
  totalReviews?: number;
}

// Real Google Reviews data from customers
const mockGoogleReviews: GoogleReview[] = [
  {
    id: "1",
    authorName: "Kamaljot Kaur",
    rating: 5,
    text: "I would give 5 star for the quality and customer service everytime i send gifts from here and m fully satisfied with quality and service",
    time: "2024-11-15T10:30:00Z",
    relativeTime: "2 months ago",
  },
  {
    id: "2",
    authorName: "Nishtha Kaur",
    rating: 5,
    text: "So good budgets, good behaviour, and amazing shop 👍👍",
    time: "2024-12-15T14:20:00Z",
    relativeTime: "a month ago",
  },
  {
    id: "3",
    authorName: "Harshdeep Mahi",
    rating: 5,
    text: "Benipal toy store is one and only store in our city which provides us best quality toys, vast variety of toys and best customer service. As they have toys for every age group kids. 👍 thumbs up for the store. Highly recommend place to buy toys for your kidz",
    time: "2023-12-15T16:45:00Z",
    relativeTime: "a year ago",
  },
  {
    id: "4",
    authorName: "Gurdeep singh Mahi",
    rating: 5,
    text: "Best customer service good quality",
    time: "2023-12-10T11:15:00Z",
    relativeTime: "a year ago",
  },
  {
    id: "5",
    authorName: "neeti kakkar",
    rating: 5,
    text: "Benipal toys is best gift shop in the city. It is the only shop where we can find toys for all age groups n gift items. They have variety which we cant find anywhere else.",
    time: "2023-12-05T09:30:00Z",
    relativeTime: "a year ago",
  },
  {
    id: "6",
    authorName: "Bhupinder Singh Manela",
    rating: 5,
    text: "Pocket Friendly Best Shop For you Kids",
    time: "2024-09-15T13:20:00Z",
    relativeTime: "4 months ago",
  },
  {
    id: "7",
    authorName: "Dalvir Singh",
    rating: 5,
    text: "Good quality best toys",
    time: "2023-12-01T13:20:00Z",
    relativeTime: "a year ago",
  },
  {
    id: "8",
    authorName: "bhagel singh",
    rating: 5,
    text: "Best place to purchase toys and gifts",
    time: "2022-12-15T13:20:00Z",
    relativeTime: "2 years ago",
  },
  {
    id: "9",
    authorName: "Balvir Singh",
    rating: 5,
    text: "Best toys & awesome gift collection must try. Best in our khamanon city. Good quality 👌👍👍",
    time: "2023-12-20T13:20:00Z",
    relativeTime: "a year ago",
  },
  {
    id: "10",
    authorName: "ABHISHEK SHRIVASTVA",
    rating: 5,
    text: "Toys quality are very good, very much satisfied & happy to buy products from here, and also their service is excellent",
    time: "2022-12-10T13:20:00Z",
    relativeTime: "2 years ago",
  },
  {
    id: "11",
    authorName: "Randeep Dhindsa",
    rating: 5,
    text: "Excellent quality and best toy shop. Highly recommended.",
    time: "2022-12-05T13:20:00Z",
    relativeTime: "2 years ago",
  },
  {
    id: "12",
    authorName: "aman joshi",
    rating: 5,
    text: "Great quality toys 👌👌💯💯 and never false promise to customers😊😊",
    time: "2022-12-01T13:20:00Z",
    relativeTime: "2 years ago",
  },
];

export const GoogleReviewsSection: React.FC<GoogleReviewsSectionProps> = ({
  reviews = mockGoogleReviews,
  averageRating = 4.9,
  totalReviews = 43,
}) => {
  const [showAllReviews, setShowAllReviews] = useState(false);

  // Show only first 3 reviews initially, or all if showAllReviews is true
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        // Full star
        stars.push(
          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        // Half star
        stars.push(
          <div key={i} className="relative h-4 w-4">
            <Star className="h-4 w-4 text-gray-300" />
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: "50%" }}
            >
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            </div>
          </div>
        );
      } else {
        // Empty star
        stars.push(<Star key={i} className="h-4 w-4 text-gray-300" />);
      }
    }
    return stars;
  };

  return (
    <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Star className="h-8 w-8 text-yellow-500 mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Customer Reviews
            </h2>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            See what our customers are saying about Benipal Toys & Gift Store!
          </p>

          {/* Overall Rating */}
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="flex items-center">
              {renderStars(Math.floor(averageRating))}
            </div>
            <span className="text-2xl font-bold text-gray-900">
              {averageRating}
            </span>
            <span className="text-gray-600">({totalReviews} reviews)</span>
          </div>

          {/* Google Reviews Link */}
          <a
            href="https://www.google.com/maps/place/Benipal+Toys+%26+Gift+Store/@30.8166613,76.3457795,17z/data=!3m1!4b1!4m6!3m5!1s0x39100726df24c193:0xa31a5429dc21f167!8m2!3d30.8166613!4d76.3457795!16s%2Fg%2F11c0x8x8x8x"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200"
          >
            <MapPin className="h-5 w-5 mr-2" />
            View on Google Maps
            <ExternalLink className="h-4 w-4 ml-1" />
          </a>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedReviews.map((review) => (
            <div
              key={review.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6"
            >
              {/* Review Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {review.authorName.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {review.authorName}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {review.relativeTime}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  {renderStars(review.rating)}
                </div>
              </div>

              {/* Review Text */}
              <div className="relative">
                <Quote className="absolute -top-2 -left-2 h-6 w-6 text-gray-300" />
                <p className="text-gray-700 text-sm leading-relaxed pl-4">
                  {review.text}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Show More/Less Button */}
        {reviews.length > 3 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAllReviews(!showAllReviews)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              {showAllReviews ? (
                <>
                  Show Less Reviews
                  <ChevronDown className="h-4 w-4 ml-2 rotate-180" />
                </>
              ) : (
                <>
                  Show More Reviews ({reviews.length - 3} more)
                  <ChevronDown className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-12">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Share Your Experience
            </h3>
            <p className="text-gray-600 mb-6">
              Had a great experience at our store? We'd love to hear from you!
            </p>
            <a
              href="https://www.google.com/maps/place/Benipal+Toys+%26+Gift+Store/@30.8166613,76.3457795,17z/data=!3m1!4b1!4m6!3m5!1s0x39100726df24c193:0xa31a5429dc21f167!8m2!3d30.8166613!4d76.3457795!16s%2Fg%2F11c0x8x8x8x"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <Star className="h-5 w-5 mr-2" />
              Write a Review
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
